import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL =
  process.env.REACT_APP_API_URL || 'https://web-production-efc21.up.railway.app';

const STAGE_PROGRESS = {
  started: 5,
  ingesting: 20,
  enriching: 40,
  hop_analysis: 60,
  metrics: 80,
  scoring: 95,
  complete: 100,
};

const TIER_STYLES = {
  Low:      { background: '#ECFDF5', color: '#065F46', fontWeight: '400' },
  Moderate: { background: '#FFFBEB', color: '#92400E', fontWeight: '400' },
  Elevated: { background: '#FEF2F2', color: '#991B1B', fontWeight: '400' },
  Critical: { background: '#FEF2F2', color: '#991B1B', fontWeight: '700' },
};

// ─── Markdown → HTML ────────────────────────────────────────────────────────

function processInline(text) {
  text = text.replace(/(\*\*|__)(.+?)\1/g, '<strong>$2</strong>');
  text = text.replace(/(\*|_)(.+?)\1/g, '<em>$2</em>');
  text = text.replace(
    /`([^`]+)`/g,
    '<code style="font-family:monospace;background:#F1F5F9;padding:2px 6px;border-radius:4px">$1</code>'
  );
  return text;
}

const metricLabels = {
  'sanctions_proximity': 'Sanctions Proximity',
  'risk_label_volume_share': 'Risk Label Volume',
  'counterparty_diversity': 'Counterparty Diversity',
  'rapid_inout_flow': 'Rapid In/Out Flow',
  'stablecoin_bridge_exposure': 'Stablecoin & Bridge',
  'temporal_risk_clustering': 'Temporal Clustering',
};

function renderTable(tableLines) {
  const dataRows = tableLines.filter(
    (line) => !/^\|[\s\-:|]+\|$/.test(line)
  );
  const totalCols = dataRows.length > 0
    ? dataRows[0].replace(/^\||\|$/g, '').split('|').length
    : 0;
  let html = '<table style="width:100%;border-collapse:collapse;margin:16px 0;table-layout:fixed;word-break:break-word">';
  dataRows.forEach((row, idx) => {
    let cells = row
      .replace(/^\||\|$/g, '')
      .split('|')
      .map((c) => c.trim());
    if (idx === 0) {
      cells = cells.filter((cell) => cell.toLowerCase() !== 'notes');
      html += '<thead><tr>';
      cells.forEach((cell) => {
        const label = metricLabels[cell] || cell;
        html += `<th style="border:1px solid #E2E8F0;padding:10px 14px;text-align:left;background:#F8FAFC;word-break:break-word;overflow-wrap:break-word">${processInline(label)}</th>`;
      });
      html += '</tr></thead><tbody>';
    } else {
      if (totalCols === 5) cells = cells.slice(0, 4);
      html += '<tr>';
      cells.forEach((cell) => {
        const label = metricLabels[cell] || cell;
        html += `<td style="border:1px solid #E2E8F0;padding:10px 14px;word-break:break-word;overflow-wrap:break-word">${processInline(label)}</td>`;
      });
      html += '</tr>';
    }
  });
  html += '</tbody></table>';
  return html;
}

function convertMarkdownToHtml(md) {
  if (!md) return '';
  const lines = md.split('\n');
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Table block
    if (line.startsWith('|')) {
      const block = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        block.push(lines[i]);
        i++;
      }
      out.push(renderTable(block));
      continue;
    }

    // Empty line
    if (!line.trim()) {
      i++;
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      out.push(
        `<h3 style="color:#0F172A;font-size:15px;font-weight:600;margin:14px 0 6px">${processInline(line.slice(4))}</h3>`
      );
      i++;
      continue;
    }
    if (line.startsWith('## ')) {
      out.push(
        `<h2 style="color:#0F172A;font-size:18px;font-weight:600;margin:18px 0 8px">${processInline(line.slice(3))}</h2>`
      );
      i++;
      continue;
    }
    if (line.startsWith('# ')) {
      out.push(
        `<h1 style="color:#0F172A;font-size:22px;font-weight:700;margin:20px 0 10px">${processInline(line.slice(2))}</h1>`
      );
      i++;
      continue;
    }

    // Bullet list block
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (
        i < lines.length &&
        (lines[i].startsWith('- ') || lines[i].startsWith('* '))
      ) {
        items.push(
          `<li style="margin:4px 0">${processInline(lines[i].slice(2))}</li>`
        );
        i++;
      }
      out.push(
        `<ul style="margin:8px 0;padding-left:20px">${items.join('')}</ul>`
      );
      continue;
    }

    // Paragraph
    out.push(
      `<p style="font-size:14px;color:#1E293B;margin:8px 0;line-height:1.6">${processInline(line)}</p>`
    );
    i++;
  }

  return out.join('\n');
}

// ─── App ────────────────────────────────────────────────────────────────────

function App() {
  const [appState, setAppState] = useState('input'); // 'input' | 'analysing' | 'complete'
  const [walletAddress, setWalletAddress] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [statusLog, setStatusLog] = useState([]); // [{message, done}]
  const [report, setReport] = useState(null);
  const [tier, setTier] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif; background: #F1F5F9; }
      input:focus { outline: none; border-color: #3730A3 !important; box-shadow: 0 0 0 3px rgba(55,48,163,0.1); }
      .btn-primary:hover { background: #312E81 !important; }
      .btn-secondary:hover { background: #F8FAFC !important; }
      .btn-demo:hover { border-color: #3730A3 !important; box-shadow: 0 0 0 3px rgba(55,48,163,0.08) !important; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleAnalyse = useCallback(async () => {
    const addr = walletAddress.trim();
    if (!addr) return;

    setAppState('analysing');
    setCurrentStatus('started');
    setProgress(STAGE_PROGRESS.started);
    setStatusLog([]);
    setError(null);
    setReport(null);
    setTier(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const url = `${API_URL}/analyse?wallet_address=${encodeURIComponent(addr)}`;
      const resp = await fetch(url, { signal: controller.signal });
      if (!resp.ok) throw new Error(`Server returned HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // hold incomplete trailing line

        for (const line of lines) {
          if (!line.trim()) continue;
          let event;
          try {
            event = JSON.parse(line);
          } catch {
            continue;
          }

          const { status, message, report: reportMd, tier: eventTier } = event;
          setCurrentStatus(status);
          setProgress(STAGE_PROGRESS[status] || 0);

          if (status === 'complete') {
            setStatusLog((prev) => prev.map((item) => ({ ...item, done: true })));
            setReport(reportMd);
            setTier(eventTier);
            setAppState('complete');
          } else if (status === 'error') {
            setError(message || 'An unknown error occurred.');
            setAppState('input');
          } else {
            setStatusLog((prev) => [
              { message, done: false },
              ...prev.map((item) => ({ ...item, done: true })),
            ]);
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        setAppState('input');
      }
    }
  }, [walletAddress]);

  const handleDownload = useCallback(() => {
    if (!report) return;
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wallet-report-${walletAddress.trim()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [report, walletAddress]);

  const handleNewAnalysis = useCallback(() => {
    setAppState('input');
    setWalletAddress('');
    setStatusLog([]);
    setCurrentStatus('');
    setProgress(0);
    setReport(null);
    setTier(null);
    setError(null);
  }, []);

  const loadDemoWallet = useCallback(async (address, demoTier) => {
    const resp = await fetch(`/demo-data/${address}.json`);
    const data = await resp.json();
    setWalletAddress(address);
    setReport(data.report);
    setTier(demoTier);
    setAppState('complete');
  }, []);

  const reportHtml = report ? convertMarkdownToHtml(report) : '';
  const tierStyle = TIER_STYLES[tier] || TIER_STYLES['Moderate'];

  const cardStyle = {
    background: '#fff',
    borderRadius: 16,
    padding: 40,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  const secondaryBtnStyle = {
    background: '#fff',
    border: '1px solid #CBD5E1',
    borderRadius: 10,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 500,
    color: '#1E293B',
    cursor: 'pointer',
    transition: 'background 0.15s',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>

      {/* ── Header ── */}
      <header style={{
        height: 64,
        background: '#fff',
        borderBottom: '1px solid #CBD5E1',
        display: 'flex',
        alignItems: 'center',
        padding: '0 32px',
      }}>
        <span style={{
          fontSize: 12,
          fontWeight: 600,
          color: '#64748B',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}>
          Wallet Exposure Explorer
        </span>
      </header>

      {/* ── Main ── */}
      <main style={{ padding: '48px 24px', maxWidth: 800, margin: '0 auto' }}>

        {/* STATE 1 — INPUT */}
        {appState === 'input' && (
          <div style={cardStyle}>

            {error && (
              <div style={{
                background: '#FEF2F2',
                borderLeft: '3px solid #F59E0B',
                borderRadius: 8,
                padding: '12px 16px',
                marginBottom: 20,
              }}>
                {(error.includes('Max calls per sec rate limit') || error.includes('NOTOK')) ? (
                  <>
                    <p style={{ fontSize: 14, color: '#92400E', fontWeight: 600 }}>
                      Rate limit reached — please wait a moment
                    </p>
                    <p style={{ fontSize: 13, color: '#92400E', marginTop: 6 }}>
                      This tool uses Etherscan's free API tier, which limits how many
                      requests can be made in quick succession. This is a known limitation
                      of the MVP. Please wait 60 seconds and try again — the analysis
                      engine is working correctly.
                    </p>
                  </>
                ) : (
                  <p style={{ fontSize: 14, color: '#991B1B' }}>
                    Something went wrong: {error}
                  </p>
                )}
              </div>
            )}

            {/* ── Section 1: Demo wallets ── */}
            <p style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', marginBottom: 6 }}>
              Try a demo wallet
            </p>
            <p style={{ fontSize: 13, color: '#64748B', marginBottom: 16 }}>
              Pre-analysed wallets that load instantly — no API call required.
            </p>

            <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
              <button
                className="btn-demo"
                onClick={() => loadDemoWallet('0x00000000000000c0d7d3017b342ff039b55b0879', 'Low')}
                style={{
                  flex: 1,
                  minWidth: 200,
                  background: '#fff',
                  border: '1px solid #CBD5E1',
                  borderRadius: 12,
                  padding: 16,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>Clean Wallet</span>
                  <span style={{ ...TIER_STYLES['Low'], borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>Low</span>
                </div>
                <p style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                  Low-activity wallet — no sanctions exposure
                </p>
              </button>

              <button
                className="btn-demo"
                onClick={() => loadDemoWallet('0x098b716b8aaf21512996dc57eb0615e2383e2f96', 'Critical')}
                style={{
                  flex: 1,
                  minWidth: 200,
                  background: '#fff',
                  border: '1px solid #CBD5E1',
                  borderRadius: 12,
                  padding: 16,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>Lazarus Group</span>
                  <span style={{ ...TIER_STYLES['Critical'], borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>Critical</span>
                </div>
                <p style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                  OFAC-sanctioned — North Korea state hacking
                </p>
              </button>
            </div>

            {/* ── Divider ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
              <span style={{ fontSize: 12, textTransform: 'uppercase', color: '#94A3B8', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
                or analyse your own wallet
              </span>
              <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
            </div>

            {/* ── Section 2: Own wallet ── */}
            <input
              type="text"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAnalyse()}
              placeholder="0x..."
              style={{
                width: '100%',
                display: 'block',
                border: '1px solid #CBD5E1',
                borderRadius: 10,
                padding: '12px 16px',
                fontSize: 15,
                color: '#1E293B',
                marginBottom: 16,
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
            />

            <button
              className="btn-primary"
              onClick={handleAnalyse}
              style={{
                width: '100%',
                height: 48,
                background: '#3730A3',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
            >
              Analyse
            </button>

            <p style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', marginTop: 8 }}>
              Analysis takes 2–5 minutes. Etherscan rate limits may require a 60-second wait between runs.
            </p>
          </div>
        )}

        {/* STATE 2 — ANALYSING */}
        {appState === 'analysing' && (
          <div style={cardStyle}>
            <p style={{
              fontSize: 14,
              color: '#64748B',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 500,
              marginBottom: 16,
            }}>
              {currentStatus.replace(/_/g, ' ')}
            </p>

            {/* Progress bar */}
            <div style={{
              height: 6,
              background: '#E2E8F0',
              borderRadius: 3,
              marginBottom: 28,
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                background: '#3730A3',
                borderRadius: 3,
                width: `${progress}%`,
                transition: 'width 0.5s ease',
              }} />
            </div>

            {/* Status log — newest at top */}
            <div>
              {statusLog.map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: '#64748B',
                  padding: '5px 0',
                }}>
                  <span style={{
                    color: item.done ? '#22C55E' : '#94A3B8',
                    minWidth: 14,
                    fontWeight: item.done ? 600 : 400,
                  }}>
                    {item.done ? '✓' : '·'}
                  </span>
                  {item.message}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STATE 3 — COMPLETE */}
        {appState === 'complete' && report && (
          <>
            {/* Summary + actions */}
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 28,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              marginBottom: 24,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 16,
            }}>
              <div>
                <span style={{
                  ...tierStyle,
                  display: 'inline-block',
                  borderRadius: 8,
                  padding: '6px 14px',
                  fontSize: 14,
                  marginBottom: 10,
                }}>
                  {tier || 'Unknown'} Risk
                </span>
                <p style={{ fontSize: 13, color: '#64748B' }}>
                  Wallet:{' '}
                  <code style={{
                    fontFamily: 'monospace',
                    background: '#F1F5F9',
                    padding: '2px 6px',
                    borderRadius: 4,
                  }}>
                    {walletAddress.trim()}
                  </code>
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  className="btn-secondary"
                  onClick={handleDownload}
                  style={secondaryBtnStyle}
                >
                  Download Report (.md)
                </button>
                <button
                  className="btn-secondary"
                  onClick={handleNewAnalysis}
                  style={secondaryBtnStyle}
                >
                  New Analysis
                </button>
              </div>
            </div>

            {/* Rendered report */}
            <div
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: 40,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                fontSize: 14,
                color: '#1E293B',
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: reportHtml }}
            />
          </>
        )}
      </main>
    </div>
  );
}

export default App;
